import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { RRule } from 'rrule';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { CardView, Spacer } from 'design-system';
import { AppView } from 'design-system/AppView';
import BottomPanel from 'design-system/BottomPanel';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { DaysOfTheMonthSelector } from 'design-system/date';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { savePotsOnboardingState } from 'features/pots/actions';
import useNavigateToNextScreenForPotsOnboarding from 'features/pots/hooks/useNavigateToNextScreenForPotsOnboarding';
import { useUpdateAutosaveCollectionFrequency } from 'features/pots/hooks/useUpdateAutosaveCollectionFrequency';
import { selectAutosaveCollectionMonthlyRRule, selectAutosaveRulesArr } from 'features/pots/reducers/selectors';
import Amplitude from 'utils/amplitude';
import createStyleSheets from 'utils/createStyleSheets';
import { getOrdinal } from 'utils/formatting';
const AutosaveMonthlyCollectionDate = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const dispatch = useAppDispatch();
    const styles = useStyles(styleSet);
    const autosaveId = useAppSelector((state) => state.pots.savingsPlan?.id);
    const autosaveCollectionMonthlyRRule = useAppSelector(selectAutosaveCollectionMonthlyRRule);
    const isEditFrequency = route.params && 'isEditFrequency' in route.params ? route.params.isEditFrequency : false;
    const rules = useAppSelector(selectAutosaveRulesArr);
    const { updateCollectionFrequency, fetching } = useUpdateAutosaveCollectionFrequency(true);
    const hasOnboardingState = useAppSelector((state) => !!state.pots.onboarding);
    const navigateToNextScreen = useNavigateToNextScreenForPotsOnboarding();
    const [selected, setSelected] = useState(autosaveCollectionMonthlyRRule?.day || 1);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_ONBOARDING}/>,
        });
    }, []);
    useEffect(() => {
        if (hasOnboardingState) {
            Amplitude.logEvent('Select Collection Date', { source: 'save' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleChange = useCallback((days) => {
        setSelected(days[0]);
    }, []);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const onSubmit = useCallback(async () => {
        const rrule = new RRule({
            freq: RRule.MONTHLY,
            bymonthday: selected,
        }).toString();
        if (hasOnboardingState) {
            dispatch(savePotsOnboardingState({ collectionFrequency: { rrule } }));
            navigateToNextScreen();
            return;
        }
        if (autosaveId) {
            if (selected === autosaveCollectionMonthlyRRule?.day) {
                navigation.popToTop();
                return;
            }
            if (isEditFrequency && rules.length) {
                updateCollectionFrequency(autosaveId, rrule, rules);
                return;
            }
        }
        navigation.navigateDeprecated('AutosaveChooseSavingRules', {
            collectionFrequency: {
                rrule,
            },
        });
    }, [
        autosaveCollectionMonthlyRRule?.day,
        autosaveId,
        hasOnboardingState,
        isEditFrequency,
        navigateToNextScreen,
        rules,
        selected,
        updateCollectionFrequency,
    ]);
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView contentContainerStyle={paddingBottom} onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader preTitle="Fixed monthly date" title="Select your collection date" subTitle="Select which day of the month to save"/>
          <CardView style={styles.calendarContainer} noMarginTop>
            <DaysOfTheMonthSelector dayStyles={styles.dayStyles} days={[selected]} maxNumberOfDays={1} totalDays={28} onChange={handleChange}/>
          </CardView>
        </Animated.ScrollView>
        <BottomPanel>
          <View style={styles.bottomPanelTextContainer}>
            <Text TextThin-16 Secondary centered>
              Your autosave will occur on the{' '}
              <Text Text-16 Secondary>
                {getOrdinal(selected)} day of each month
              </Text>
            </Text>
          </View>
          <Spacer h={16}/>
          <Button brand onPress={onSubmit} title="Continue" isFetching={fetching}/>
        </BottomPanel>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    calendarContainer: {
        paddingHorizontal: rem(12),
        paddingTop: rem(16),
    },
    bottomPanelTextContainer: {
        paddingHorizontal: rem(24),
    },
    dayStyles: {
        backgroundColor: colors.background.dark,
    },
}));
export default AutosaveMonthlyCollectionDate;
